/** Compass bearing helpers and frame/sun/light GeoJSON. */
import { offsetByBearing, isPointIlluminated, type BuildingFeature, type LngLat } from './shadows'
import { getSunTimes, sunPosition } from './sun'

export function frameArrowGeoJSON(
  lng: number,
  lat: number,
  bearing: number,
  lengthM = 90,
): GeoJSON.FeatureCollection {
  const tip = offsetByBearing(lng, lat, lengthM, bearing)
  const left = offsetByBearing(tip[0], tip[1], 20, bearing + 150)
  const right = offsetByBearing(tip[0], tip[1], 20, bearing - 150)
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { kind: 'frame' },
        geometry: {
          type: 'LineString',
          coordinates: [[lng, lat], tip, left, tip, right],
        },
      },
    ],
  }
}

/**
 * Map ground radius for the sun disc.
 * Low sun sits on the horizon ring; high sun pulls inward so elevation is readable.
 */
function elevRadius(horizonM: number, altitudeDeg: number): number {
  const alt = Math.max(0, Math.min(85, altitudeDeg))
  const t = Math.cos((alt * Math.PI) / 180)
  return horizonM * Math.max(0.22, 0.28 + 0.72 * t)
}

/** Warmth 0 (noon) → 1 (sunrise/sunset) for styling. */
function warmthFromAltitude(altitudeDeg: number): number {
  if (altitudeDeg <= 0) return 1
  if (altitudeDeg >= 45) return 0
  return 1 - altitudeDeg / 45
}

/**
 * Horizon path of the sun + incidence beam onto the pin.
 * Day arc uses a fixed ring (azimuth) so scrubbing time shows clear east→west motion.
 * Sun disc uses elevation radius so height above horizon is also visible.
 */
export function sunRayGeoJSON(
  lng: number,
  lat: number,
  sunAzimuth: number,
  lengthM = 520,
  altitudeDeg = 30,
  when: Date = new Date(),
): GeoJSON.FeatureCollection {
  const lightFrom = (sunAzimuth + 180) % 360
  const warmth = warmthFromAltitude(altitudeDeg)
  const rElev = elevRadius(lengthM, altitudeDeg)

  // Sun on elevation radius (moves in/out with height)
  const sunPt = offsetByBearing(lng, lat, rElev, sunAzimuth)
  // Anchor on fixed horizon ring (azimuth track)
  const sunHorizon = offsetByBearing(lng, lat, lengthM, sunAzimuth)
  const mid = offsetByBearing(lng, lat, rElev * 0.55, sunAzimuth)

  // Incidence arrow at pin
  const headL = offsetByBearing(lng, lat, 38, lightFrom + 30)
  const headR = offsetByBearing(lng, lat, 38, lightFrom - 30)
  const fallTip = offsetByBearing(lng, lat, 70, lightFrom)

  // Soft beam wedge (narrower when sun is high)
  const beamHalf = 8 + warmth * 10
  const cone: LngLat[] = [[lng, lat]]
  for (let i = 0; i <= 18; i++) {
    const a = sunAzimuth - beamHalf + (2 * beamHalf * i) / 18
    cone.push(offsetByBearing(lng, lat, rElev, a))
  }
  cone.push([lng, lat])

  const coreHalf = Math.max(3, beamHalf * 0.35)
  const core: LngLat[] = [[lng, lat]]
  for (let i = 0; i <= 12; i++) {
    const a = sunAzimuth - coreHalf + (2 * coreHalf * i) / 12
    core.push(offsetByBearing(lng, lat, rElev * 0.88, a))
  }
  core.push([lng, lat])

  const times = getSunTimes(lat, lng, when)
  const pathDay: LngLat[] = []
  const pathDone: LngLat[] = []
  const markers: GeoJSON.Feature[] = []
  const t0 = times.sunrise?.getTime()
  const t1 = times.sunset?.getTime()
  const noon = times.solarNoon?.getTime()
  const nowMs = when.getTime()

  if (t0 != null && t1 != null && t1 > t0) {
    const steps = 64
    for (let i = 0; i <= steps; i++) {
      const t = t0 + ((t1 - t0) * i) / steps
      const p = sunPosition(new Date(t), lat, lng)
      // Fixed ring = clear horizon travel
      const pt = offsetByBearing(lng, lat, lengthM, p.azimuthDeg)
      pathDay.push(pt)
      if (t <= nowMs + 30_000) pathDone.push(pt)
    }

    const rise = offsetByBearing(lng, lat, lengthM, sunPosition(new Date(t0), lat, lng).azimuthDeg)
    const set = offsetByBearing(lng, lat, lengthM, sunPosition(new Date(t1), lat, lng).azimuthDeg)
    markers.push(
      {
        type: 'Feature',
        properties: { kind: 'endpoint', label: 'W', role: 'rise' },
        geometry: { type: 'Point', coordinates: rise },
      },
      {
        type: 'Feature',
        properties: { kind: 'endpoint', label: 'Z', role: 'set' },
        geometry: { type: 'Point', coordinates: set },
      },
    )

    if (noon != null) {
      const np = sunPosition(new Date(noon), lat, lng)
      markers.push({
        type: 'Feature',
        properties: { kind: 'endpoint', label: 'S', role: 'noon' },
        geometry: {
          type: 'Point',
          coordinates: offsetByBearing(lng, lat, lengthM, np.azimuthDeg),
        },
      })
    }

    // Sparse hour ticks on the ring
    for (let h = 6; h <= 20; h++) {
      const d = new Date(when)
      d.setHours(h, 0, 0, 0)
      const ms = d.getTime()
      if (ms < t0 || ms > t1) continue
      const p = sunPosition(d, lat, lng)
      if (p.altitudeDeg < 0) continue
      const outer = offsetByBearing(lng, lat, lengthM * 1.05, p.azimuthDeg)
      const inner = offsetByBearing(lng, lat, lengthM * 0.95, p.azimuthDeg)
      markers.push({
        type: 'Feature',
        properties: { kind: 'hour-tick', hour: h },
        geometry: { type: 'LineString', coordinates: [inner, outer] },
      })
    }
  }

  // Light fall streaks past the pin (direction of incidence)
  const streaks: GeoJSON.Feature[] = []
  for (const side of [-36, -18, 0, 18, 36] as const) {
    const base =
      side === 0
        ? ([lng, lat] as LngLat)
        : offsetByBearing(
            lng,
            lat,
            Math.abs(side),
            (lightFrom + (side > 0 ? 90 : -90) + 360) % 360,
          )
    streaks.push({
      type: 'Feature',
      properties: { kind: 'streak', warmth },
      geometry: {
        type: 'LineString',
        coordinates: [
          offsetByBearing(base[0], base[1], 8, lightFrom),
          offsetByBearing(base[0], base[1], 120 + warmth * 40, lightFrom),
        ],
      },
    })
  }

  const features: GeoJSON.Feature[] = [
    {
      type: 'Feature',
      properties: { kind: 'cone', warmth },
      geometry: { type: 'Polygon', coordinates: [cone] },
    },
    {
      type: 'Feature',
      properties: { kind: 'core', warmth },
      geometry: { type: 'Polygon', coordinates: [core] },
    },
  ]

  if (pathDay.length >= 2) {
    features.push({
      type: 'Feature',
      properties: { kind: 'path-day' },
      geometry: { type: 'LineString', coordinates: pathDay },
    })
  }
  if (pathDone.length >= 2) {
    features.push({
      type: 'Feature',
      properties: { kind: 'path-done' },
      geometry: { type: 'LineString', coordinates: pathDone },
    })
  }

  // Thin radial guide: horizon ring → elevated sun (shows altitude)
  if (Math.abs(rElev - lengthM) > 25) {
    features.push({
      type: 'Feature',
      properties: { kind: 'elev-guide' },
      geometry: { type: 'LineString', coordinates: [sunHorizon, sunPt] },
    })
  }

  features.push(
    ...markers,
    ...streaks,
    {
      type: 'Feature',
      properties: { kind: 'ray', warmth },
      geometry: { type: 'LineString', coordinates: [sunPt, [lng, lat]] },
    },
    {
      type: 'Feature',
      properties: { kind: 'ray-flow', warmth },
      geometry: { type: 'LineString', coordinates: [sunPt, [lng, lat]] },
    },
    {
      type: 'Feature',
      properties: { kind: 'arrow', warmth },
      geometry: {
        type: 'LineString',
        coordinates: [headL, [lng, lat], headR, fallTip],
      },
    },
    {
      type: 'Feature',
      properties: { kind: 'sun', warmth, altitude: altitudeDeg },
      geometry: { type: 'Point', coordinates: sunPt },
    },
    {
      type: 'Feature',
      properties: { kind: 'glow', warmth },
      geometry: { type: 'Point', coordinates: mid },
    },
    {
      type: 'Feature',
      properties: { kind: 'horizon-dot' },
      geometry: { type: 'Point', coordinates: sunHorizon },
    },
  )

  return { type: 'FeatureCollection', features }
}

function ringCentroid(ring: LngLat[]): LngLat {
  let x = 0
  let y = 0
  const n = Math.max(1, ring.length - 1)
  for (let i = 0; i < n; i++) {
    x += ring[i][0]
    y += ring[i][1]
  }
  return [x / n, y / n]
}

/** 3D buildings colored by sun exposure + short rays onto lit roofs. */
export function buildingsSunGeoJSON(
  buildings: BuildingFeature[],
  sunAzimuth: number,
  sunAltitude: number,
): { buildings: GeoJSON.FeatureCollection; roofRays: GeoJSON.FeatureCollection } {
  const bFeatures: GeoJSON.Feature[] = []
  const rayFeatures: GeoJSON.Feature[] = []

  let rayBudget = 40

  for (const b of buildings) {
    const [cx, cy] = ringCentroid(b.ring)
    const lit =
      sunAltitude > 0.5 &&
      isPointIlluminated(cx, cy, buildings, sunAzimuth, sunAltitude)

    bFeatures.push({
      type: 'Feature',
      properties: {
        id: b.id,
        height: b.height,
        lit: lit ? 1 : 0,
      },
      geometry: { type: 'Polygon', coordinates: [b.ring] },
    })

    if (lit && rayBudget > 0) {
      rayBudget -= 1
      const from = offsetByBearing(cx, cy, Math.min(45, 14 + b.height * 0.35), sunAzimuth)
      rayFeatures.push({
        type: 'Feature',
        properties: { kind: 'roof-ray', height: b.height },
        geometry: {
          type: 'LineString',
          coordinates: [from, [cx, cy]],
        },
      })
      rayFeatures.push({
        type: 'Feature',
        properties: { kind: 'roof-hit', height: b.height },
        geometry: { type: 'Point', coordinates: [cx, cy] },
      })
    }
  }

  return {
    buildings: { type: 'FeatureCollection', features: bFeatures },
    roofRays: { type: 'FeatureCollection', features: rayFeatures },
  }
}
